import { useState, useEffect } from 'react'
import { 
  Upload, 
  Image, 
  FileImage, 
  Tag, 
  Trash2, 
  Edit2, 
  Search, 
  Filter,
  X,
  Check,
  Plus,
  Eye,
  Download,
  Copy
} from 'lucide-react'
import api from '../lib/api'
import toast from 'react-hot-toast'

// Helper to get full asset URL (backend serves files on different port in dev)
const getAssetUrl = (url) => {
  if (!url) return ''
  // In development, prepend the API base URL
  if (url.startsWith('/uploads')) {
    // Use the backend URL - in dev mode with proxy, just use relative path
    // In production or when accessing directly, use the full backend URL
    const apiBase = import.meta.env.VITE_API_URL?.replace('/api/v1', '').replace('/api', '') || ''
    // If we have an API base, use it; otherwise use relative path (proxy will handle it)
    return apiBase ? `${apiBase}${url}` : url
  }
  return url
}

// Export for use in other components
export { getAssetUrl }

const ASSET_TYPES = [
  { value: 'diagram', label: 'Diagram', icon: '📊' },
  { value: 'image', label: 'Image', icon: '🖼️' },
  { value: 'logo', label: 'Logo', icon: '🏢' },
  { value: 'chart', label: 'Chart', icon: '📈' },
  { value: 'icon', label: 'Icon', icon: '⭐' },
  { value: 'other', label: 'Other', icon: '📁' }
]

const SECTION_OPTIONS = [
  { value: 'executive_summary', label: 'Executive Summary' },
  { value: 'introduction', label: 'Introduction' },
  { value: 'problem_statement', label: 'Problem Statement' },
  { value: 'proposed_solution', label: 'Proposed Solution' },
  { value: 'technical_approach', label: 'Technical Approach' },
  { value: 'architecture', label: 'Architecture' },
  { value: 'implementation', label: 'Implementation' },
  { value: 'timeline', label: 'Timeline' },
  { value: 'team', label: 'Team' },
  { value: 'pricing', label: 'Pricing' },
  { value: 'case_studies', label: 'Case Studies' },
  { value: 'appendix', label: 'Appendix' },
  { value: 'any', label: 'Any Section' }
]

const SUGGESTED_TAGS = [
  'architecture', 'network', 'cloud', 'database', 'security', 
  'workflow', 'process', 'timeline', 'gantt', 'organization',
  'team', 'infrastructure', 'deployment', 'integration', 'api',
  'mobile', 'web', 'dashboard', 'analytics', 'iot', 'satellite',
  'space', 'earth-observation', 'remote-sensing', 'gis'
]

export default function Assets() {
  const [assets, setAssets] = useState([])
  const [loading, setLoading] = useState(true)
  const [uploading, setUploading] = useState(false)
  const [showUploadModal, setShowUploadModal] = useState(false)
  const [showPreviewModal, setShowPreviewModal] = useState(null)
  const [editingAsset, setEditingAsset] = useState(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [filterType, setFilterType] = useState('')
  const [filterSection, setFilterSection] = useState('')

  // Upload form state
  const [uploadForm, setUploadForm] = useState({
    file: null,
    name: '',
    description: '',
    type: 'diagram',
    tags: [],
    suitableFor: ['any'],
    defaultSize: 'medium',
    alignment: 'center',
    caption: '',
    isPublic: false
  })
  const [tagInput, setTagInput] = useState('')
  const [dragActive, setDragActive] = useState(false)

  useEffect(() => {
    fetchAssets()
  }, [filterType, filterSection, searchQuery])

  const fetchAssets = async () => {
    try {
      setLoading(true)
      const params = new URLSearchParams()
      if (filterType) params.append('type', filterType)
      if (filterSection) params.append('suitableFor', filterSection)
      if (searchQuery) params.append('search', searchQuery)
      
      const response = await api.get(`/assets?${params.toString()}`)
      setAssets(response.data)
    } catch (error) {
      console.error('Failed to fetch assets:', error)
      toast.error('Failed to load assets')
    } finally {
      setLoading(false)
    }
  }

  const handleDrag = (e) => {
    e.preventDefault()
    e.stopPropagation()
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true)
    } else if (e.type === 'dragleave') {
      setDragActive(false)
    }
  }

  const handleDrop = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(false)
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files[0])
    }
  }

  const handleFileSelect = (file) => {
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/svg+xml', 'image/webp', 'application/pdf']
    
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Please upload an image (JPEG, PNG, GIF, SVG, WebP) or PDF.')
      return
    }
    
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 10MB.')
      return
    }
    
    setUploadForm(prev => ({
      ...prev,
      file,
      name: prev.name || file.name.replace(/\.[^/.]+$/, '')
    }))
  }

  const handleAddTag = (tag) => {
    const normalizedTag = tag.toLowerCase().trim()
    if (normalizedTag && !uploadForm.tags.includes(normalizedTag)) {
      setUploadForm(prev => ({
        ...prev,
        tags: [...prev.tags, normalizedTag]
      }))
    }
    setTagInput('')
  }

  const handleRemoveTag = (tagToRemove) => {
    setUploadForm(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }))
  }

  const handleSectionToggle = (section) => {
    setUploadForm(prev => {
      const current = prev.suitableFor
      if (current.includes(section)) {
        return { ...prev, suitableFor: current.filter(s => s !== section) }
      } else {
        return { ...prev, suitableFor: [...current, section] }
      }
    })
  }

  const handleUpload = async () => {
    if (!uploadForm.file) {
      toast.error('Please select a file to upload')
      return
    }

    try {
      setUploading(true)
      
      const formData = new FormData()
      formData.append('file', uploadForm.file)
      formData.append('name', uploadForm.name)
      formData.append('description', uploadForm.description)
      formData.append('type', uploadForm.type)
      formData.append('tags', uploadForm.tags.join(','))
      formData.append('suitableFor', uploadForm.suitableFor.join(','))
      formData.append('defaultSize', uploadForm.defaultSize)
      formData.append('alignment', uploadForm.alignment)
      formData.append('caption', uploadForm.caption)
      formData.append('isPublic', uploadForm.isPublic)

      await api.post('/assets/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })

      toast.success('Asset uploaded successfully!')
      setShowUploadModal(false)
      resetUploadForm()
      fetchAssets()
    } catch (error) {
      console.error('Upload failed:', error)
      toast.error(error.response?.data?.error || 'Failed to upload asset')
    } finally {
      setUploading(false)
    }
  }

  const resetUploadForm = () => {
    setUploadForm({
      file: null,
      name: '',
      description: '',
      type: 'diagram',
      tags: [],
      suitableFor: ['any'],
      defaultSize: 'medium',
      alignment: 'center',
      caption: '',
      isPublic: false
    })
    setTagInput('')
  }

  const handleDelete = async (assetId) => {
    if (!confirm('Are you sure you want to delete this asset?')) return

    try {
      await api.delete(`/assets/${assetId}`)
      toast.success('Asset deleted')
      fetchAssets()
    } catch (error) {
      toast.error('Failed to delete asset')
    }
  }

  const handleUpdateAsset = async (assetId, updates) => {
    try {
      await api.put(`/assets/${assetId}`, updates)
      toast.success('Asset updated')
      setEditingAsset(null)
      fetchAssets()
    } catch (error) {
      toast.error('Failed to update asset')
    }
  }

  const copyAssetUrl = (asset) => {
    const url = `${window.location.origin}${asset.url}`
    navigator.clipboard.writeText(url)
    toast.success('URL copied to clipboard')
  }

  const getFilePreview = (file) => {
    if (!file) return null
    if (file.type === 'application/pdf') {
      return <FileImage className="w-16 h-16 text-gray-400" />
    }
    return URL.createObjectURL(file)
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Company Assets</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Upload and manage diagrams, images, and other assets for proposals
          </p>
        </div>
        <button
          onClick={() => setShowUploadModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Upload className="w-5 h-5" />
          Upload Asset
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4 mb-6">
        <div className="flex-1 min-w-[200px]">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search assets..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
        
        <select
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
          className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
        >
          <option value="">All Types</option>
          {ASSET_TYPES.map(type => (
            <option key={type.value} value={type.value}>{type.icon} {type.label}</option>
          ))}
        </select>

        <select
          value={filterSection}
          onChange={(e) => setFilterSection(e.target.value)}
          className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
        >
          <option value="">All Sections</option>
          {SECTION_OPTIONS.map(section => (
            <option key={section.value} value={section.value}>{section.label}</option>
          ))}
        </select>
      </div>

      {/* Assets Grid */}
      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      ) : assets.length === 0 ? (
        <div className="text-center py-16 bg-gray-50 dark:bg-gray-800 rounded-lg">
          <Image className="w-16 h-16 mx-auto text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No assets yet</h3>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            Upload your first asset to get started
          </p>
          <button
            onClick={() => setShowUploadModal(true)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <Upload className="w-5 h-5" />
            Upload Asset
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {assets.map(asset => (
            <div
              key={asset._id}
              className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden group"
            >
              {/* Preview */}
              <div 
                className="relative h-40 bg-gray-100 dark:bg-gray-700 flex items-center justify-center cursor-pointer"
                onClick={() => setShowPreviewModal(asset)}
              >
                {asset.mimetype?.startsWith('image/') ? (
                  <img
                    src={getAssetUrl(asset.url)}
                    alt={asset.name}
                    className="w-full h-full object-contain"
                  />
                ) : (
                  <FileImage className="w-16 h-16 text-gray-400" />
                )}
                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                  <button className="p-2 bg-white rounded-full text-gray-700 hover:bg-gray-100">
                    <Eye className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* Info */}
              <div className="p-4">
                <div className="flex items-start justify-between mb-2">
                  <h3 className="font-medium text-gray-900 dark:text-white truncate flex-1">
                    {asset.name}
                  </h3>
                  <span className="text-xs px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded text-gray-600 dark:text-gray-400 ml-2">
                    {ASSET_TYPES.find(t => t.value === asset.type)?.icon} {asset.type}
                  </span>
                </div>

                {/* Tags */}
                {asset.tags?.length > 0 && (
                  <div className="flex flex-wrap gap-1 mb-3">
                    {asset.tags.slice(0, 3).map(tag => (
                      <span
                        key={tag}
                        className="text-xs px-2 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded"
                      >
                        {tag}
                      </span>
                    ))}
                    {asset.tags.length > 3 && (
                      <span className="text-xs text-gray-500">+{asset.tags.length - 3}</span>
                    )}
                  </div>
                )}

                {/* Suitable For */}
                <div className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                  For: {asset.suitableFor?.map(s => SECTION_OPTIONS.find(o => o.value === s)?.label || s).join(', ')}
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2 pt-2 border-t border-gray-100 dark:border-gray-700">
                  <button
                    onClick={() => copyAssetUrl(asset)}
                    className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded"
                    title="Copy URL"
                  >
                    <Copy className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setEditingAsset(asset)}
                    className="p-1.5 text-gray-500 hover:text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 rounded"
                    title="Edit"
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(asset._id)}
                    className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
                    title="Delete"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                  <span className="ml-auto text-xs text-gray-400">
                    Used {asset.usageCount || 0}x
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Upload Asset</h2>
              <button
                onClick={() => { setShowUploadModal(false); resetUploadForm(); }}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* File Drop Zone */}
              <div
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                  dragActive 
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' 
                    : 'border-gray-300 dark:border-gray-600 hover:border-blue-400'
                }`}
              >
                {uploadForm.file ? (
                  <div className="space-y-3">
                    {uploadForm.file.type.startsWith('image/') ? (
                      <img
                        src={getFilePreview(uploadForm.file)}
                        alt="Preview"
                        className="max-h-32 mx-auto rounded"
                      />
                    ) : (
                      <FileImage className="w-16 h-16 mx-auto text-gray-400" />
                    )}
                    <p className="text-sm text-gray-600 dark:text-gray-400">{uploadForm.file.name}</p>
                    <button
                      onClick={() => setUploadForm(prev => ({ ...prev, file: null }))}
                      className="text-sm text-red-600 hover:text-red-700"
                    >
                      Remove
                    </button>
                  </div>
                ) : (
                  <>
                    <Upload className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                    <p className="text-gray-600 dark:text-gray-400 mb-2">
                      Drag and drop your file here, or
                    </p>
                    <label className="inline-block px-4 py-2 bg-blue-600 text-white rounded-lg cursor-pointer hover:bg-blue-700">
                      Browse Files
                      <input
                        type="file"
                        accept="image/*,.pdf"
                        onChange={(e) => e.target.files?.[0] && handleFileSelect(e.target.files[0])}
                        className="hidden"
                      />
                    </label>
                    <p className="text-xs text-gray-500 mt-2">
                      Supports: JPEG, PNG, GIF, SVG, WebP, PDF (max 10MB)
                    </p>
                  </>
                )}
              </div>

              {/* Name & Description */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Name *
                  </label>
                  <input
                    type="text"
                    value={uploadForm.name}
                    onChange={(e) => setUploadForm(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="Asset name"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Type
                  </label>
                  <select
                    value={uploadForm.type}
                    onChange={(e) => setUploadForm(prev => ({ ...prev, type: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    {ASSET_TYPES.map(type => (
                      <option key={type.value} value={type.value}>{type.icon} {type.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Description
                </label>
                <textarea
                  value={uploadForm.description}
                  onChange={(e) => setUploadForm(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Brief description of this asset"
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>

              {/* Tags */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Tags (for AI matching)
                </label>
                <div className="flex flex-wrap gap-2 mb-2">
                  {uploadForm.tags.map(tag => (
                    <span
                      key={tag}
                      className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded text-sm"
                    >
                      {tag}
                      <button onClick={() => handleRemoveTag(tag)} className="hover:text-blue-900">
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTag(tagInput))}
                    placeholder="Add a tag..."
                    className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                  <button
                    onClick={() => handleAddTag(tagInput)}
                    className="px-3 py-2 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600"
                  >
                    <Plus className="w-5 h-5" />
                  </button>
                </div>
                <div className="flex flex-wrap gap-1 mt-2">
                  <span className="text-xs text-gray-500">Suggestions:</span>
                  {SUGGESTED_TAGS.filter(t => !uploadForm.tags.includes(t)).slice(0, 8).map(tag => (
                    <button
                      key={tag}
                      onClick={() => handleAddTag(tag)}
                      className="text-xs px-2 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded hover:bg-gray-200 dark:hover:bg-gray-600"
                    >
                      +{tag}
                    </button>
                  ))}
                </div>
              </div>

              {/* Suitable For Sections */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Suitable for Sections
                </label>
                <div className="flex flex-wrap gap-2">
                  {SECTION_OPTIONS.map(section => (
                    <button
                      key={section.value}
                      onClick={() => handleSectionToggle(section.value)}
                      className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
                        uploadForm.suitableFor.includes(section.value)
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                      }`}
                    >
                      {section.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Display Settings */}
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Default Size
                  </label>
                  <select
                    value={uploadForm.defaultSize}
                    onChange={(e) => setUploadForm(prev => ({ ...prev, defaultSize: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    <option value="small">Small</option>
                    <option value="medium">Medium</option>
                    <option value="large">Large</option>
                    <option value="full-width">Full Width</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Alignment
                  </label>
                  <select
                    value={uploadForm.alignment}
                    onChange={(e) => setUploadForm(prev => ({ ...prev, alignment: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    <option value="left">Left</option>
                    <option value="center">Center</option>
                    <option value="right">Right</option>
                  </select>
                </div>
                <div className="flex items-end">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={uploadForm.isPublic}
                      onChange={(e) => setUploadForm(prev => ({ ...prev, isPublic: e.target.checked }))}
                      className="w-4 h-4 rounded border-gray-300"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">Public asset</span>
                  </label>
                </div>
              </div>

              {/* Caption */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Caption (optional)
                </label>
                <input
                  type="text"
                  value={uploadForm.caption}
                  onChange={(e) => setUploadForm(prev => ({ ...prev, caption: e.target.value }))}
                  placeholder="Caption to display with the asset"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
              <button
                onClick={() => { setShowUploadModal(false); resetUploadForm(); }}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={handleUpload}
                disabled={!uploadForm.file || !uploadForm.name || uploading}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {uploading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4" />
                    Upload
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      {showPreviewModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={() => setShowPreviewModal(null)}>
          <div className="max-w-4xl max-h-[90vh] overflow-auto" onClick={e => e.stopPropagation()}>
            {showPreviewModal.mimetype?.startsWith('image/') ? (
              <img
                src={getAssetUrl(showPreviewModal.url)}
                alt={showPreviewModal.name}
                className="max-w-full max-h-[80vh] object-contain rounded-lg"
              />
            ) : (
              <div className="bg-white dark:bg-gray-800 p-8 rounded-lg text-center">
                <FileImage className="w-24 h-24 mx-auto text-gray-400 mb-4" />
                <p className="text-gray-600 dark:text-gray-400">PDF Preview not available</p>
                <a
                  href={getAssetUrl(showPreviewModal.url)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  <Download className="w-4 h-4" />
                  Download PDF
                </a>
              </div>
            )}
            <div className="mt-4 text-center">
              <h3 className="text-white text-lg font-medium">{showPreviewModal.name}</h3>
              {showPreviewModal.displaySettings?.caption && (
                <p className="text-gray-300 text-sm mt-1">{showPreviewModal.displaySettings.caption}</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editingAsset && (
        <EditAssetModal
          asset={editingAsset}
          onClose={() => setEditingAsset(null)}
          onSave={handleUpdateAsset}
        />
      )}
    </div>
  )
}

function EditAssetModal({ asset, onClose, onSave }) {
  const [form, setForm] = useState({
    name: asset.name,
    description: asset.description || '',
    type: asset.type,
    tags: asset.tags || [],
    suitableFor: asset.suitableFor || ['any'],
    displaySettings: asset.displaySettings || {},
    isPublic: asset.isPublic || false
  })
  const [tagInput, setTagInput] = useState('')
  const [saving, setSaving] = useState(false)

  const handleSave = async () => {
    setSaving(true)
    await onSave(asset._id, form)
    setSaving(false)
  }

  const handleAddTag = (tag) => {
    const normalizedTag = tag.toLowerCase().trim()
    if (normalizedTag && !form.tags.includes(normalizedTag)) {
      setForm(prev => ({ ...prev, tags: [...prev.tags, normalizedTag] }))
    }
    setTagInput('')
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-lg w-full">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Edit Asset</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Name</label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Tags</label>
            <div className="flex flex-wrap gap-2 mb-2">
              {form.tags.map(tag => (
                <span key={tag} className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded text-sm">
                  {tag}
                  <button onClick={() => setForm(prev => ({ ...prev, tags: prev.tags.filter(t => t !== tag) }))}>
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <input
                type="text"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTag(tagInput))}
                placeholder="Add tag..."
                className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
              <button onClick={() => handleAddTag(tagInput)} className="px-3 py-2 bg-gray-100 dark:bg-gray-700 rounded-lg">
                <Plus className="w-5 h-5" />
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Suitable For</label>
            <div className="flex flex-wrap gap-2">
              {SECTION_OPTIONS.map(section => (
                <button
                  key={section.value}
                  onClick={() => {
                    setForm(prev => ({
                      ...prev,
                      suitableFor: prev.suitableFor.includes(section.value)
                        ? prev.suitableFor.filter(s => s !== section.value)
                        : [...prev.suitableFor, section.value]
                    }))
                  }}
                  className={`px-3 py-1 rounded text-sm ${
                    form.suitableFor.includes(section.value)
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                  }`}
                >
                  {section.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  )
}
